import { SagaIterator } from 'redux-saga';
import { call, put, select, delay } from 'redux-saga/effects';
import Api from '../../utils/API';
import {
  errorsGlobalError,
  fetchCouponsSuccessAction,
  fetchLaboratoriesSuccessAction,
  fetchOrderImagesSuccessAction,
  fetchOrderListForUserSuccessAction,
  fetchPresetsSuccessAction,
  fetchProductGroupsSuccessAction,
  fetchProductPricingListSuccessAction,
  fetchProductsSuccessAction,
  fetchProductUserPricingGroupsListSuccessAction,
  fetchUserAccountBalanceSuccessAction,
  fetchUserPricingAction,
  fetchUserPricingSuccessAction,
  getGlobalShopSettingsSuccess,
  setPayDocumentsSuccessAction,
  setSalesAutomationAction,
  setSelectedPriceListAction,
  showNotifyFail,
  showNotifySuccess,
  userHasEnoughInvoicingDataSuccessAction
} from '../../actions';
import ApiErrors from '../../utils/API/APIErrors';
import {
  ICreateGlobalShopSettingsParams,
  IFetchOrderListForUserParams,
  ICreateProductPriceListParams,
  IPriceItem,
  ISagaAction,
  IShopCoupon,
  IUpdateGlobalShopSettingsParams,
  IUpdatePriceValue,
  IDeleteProductPriceListParams,
  ISalesConfigBulkUpdateAction,
  ICancelOrderParams,
  IShopOrder,
  IPricingListItem,
  IPricingGroup,
  IUpdatePricingGroup,
  IRenameProductPricingListParams,
  ICreateDigitalProduct,
  IUpdateProductUserPricings,
  IDeleteDigitalUserPricing,
  IShopPayDocumentsAction
} from '../../types';
import {
  getPricingGroups,
  getProductPrisingListSelector,
  getProductUserPricing,
  getSelectedPriceListSelector,
  getShopCouponsSelector,
  getShopOrdersSelector,
  getUser
} from '../../selectors';
import { pickBy, omit } from 'lodash';
import moment from 'moment';
import { getTranslationKey } from '../../utils';
import { setModalOpenState } from '../../../actions';
import { handleOfflineResponse } from '../global';

interface ApiError extends Error {
  code?: number;
  response?: {
    data?: any;
  };
}

export const fetchProductPricingListSaga = function* (): SagaIterator {
  try {
    const response: IPricingListItem[] = yield call(Api.Shop.getProductPricingLists);
    ApiErrors.checkOnApiError(response);

    const currentSelectedPriceListId: string = yield select(getSelectedPriceListSelector);
    const selected = response.find((item) => item.selected) || null;

    if (!selected) {
      const defaultItem = response.find((item) => item.default) || response[0];
      if (defaultItem) {
        yield put(setSelectedPriceListAction(defaultItem._id));
      }
    }

    if (selected && selected._id !== currentSelectedPriceListId) {
      yield put(setSelectedPriceListAction(selected._id));
    }

    yield put(fetchProductPricingListSuccessAction(response));
  } catch (e) {
    yield put(errorsGlobalError(e as ErrorEvent));
  }
};

export const createProductPriceListSaga = function* ({
  payload
}: ISagaAction<ICreateProductPriceListParams>): SagaIterator {
  try {
    const pricesList = yield select(getProductPrisingListSelector);
    const response = yield call(Api.Shop.createProductPricingList, payload);
    ApiErrors.checkOnApiError(response);
    pricesList.push(response);
    yield put(fetchProductPricingListSuccessAction(pricesList));
    yield put(setSelectedPriceListAction(response._id));
  } catch (e) {
    yield put(errorsGlobalError(e as ErrorEvent));
  }
};

export const createProductPriceListSaga2 = function* ({
  payload
}: ISagaAction<ICreateProductPriceListParams>): SagaIterator {
  try {
    const pricesList = yield select(getProductPrisingListSelector);
    const response = yield call(Api.Shop.createProductPricingList2, payload);
    ApiErrors.checkOnApiError(response);
    pricesList.push(response);
    yield put(fetchProductPricingListSuccessAction(pricesList));
    yield put(setSelectedPriceListAction(response._id));
  } catch (e) {
    yield put(errorsGlobalError(e as ErrorEvent));
  }
};
export const setSelectedPriceListSaga = function* ({ payload }: ISagaAction<string>): SagaIterator {
  try {
    const response = yield call(Api.Shop.setSelectedProductPricingList, payload);
    ApiErrors.checkOnApiError(response);
  } catch (e) {
    yield put(errorsGlobalError(e as ErrorEvent));
  }
};

export const renameProductPricingListSaga = function* ({
  payload
}: ISagaAction<IRenameProductPricingListParams>): SagaIterator {
  try {
    const response = yield call(Api.Shop.renameProductPricingList, payload);
    ApiErrors.checkOnApiError(response);

    const pricesLists = yield select(getProductPrisingListSelector);
    const updatedPriceLists = pricesLists.map((list: IPricingListItem) =>
      list._id === payload.pricingListId ? { ...list, name: payload.name } : list
    );

    yield put(fetchProductPricingListSuccessAction(updatedPriceLists));
    yield put(
      showNotifySuccess({
        message: getTranslationKey('shop.pricingLists.renameModal.successMessage')
      })
    );
  } catch (error) {
    yield put(showNotifyFail());
    yield put(errorsGlobalError(error as ErrorEvent));
  }
};
export const deleteProductPriceListSaga = function* ({
  payload
}: ISagaAction<IDeleteProductPriceListParams>): SagaIterator {
  try {
    const pricesList = yield select(getProductPrisingListSelector);
    const response = yield call(Api.Shop.deleteProductPricingList, payload);
    ApiErrors.checkOnApiError(response);

    const otherPricingLists = pricesList.filter((pricesList: any) => pricesList._id !== payload.id);
    const defaultList =
      otherPricingLists.find((pricesList: any) => pricesList.default) || otherPricingLists[0];

    if (defaultList) yield put(setSelectedPriceListAction(defaultList._id));

    yield put(
      fetchProductPricingListSuccessAction(
        pricesList.filter((pricesList: any) => pricesList._id !== payload.id)
      )
    );
    const presetResponse = yield call(Api.Presets.get);
    ApiErrors.checkOnApiError(presetResponse);
    yield put(fetchPresetsSuccessAction(presetResponse));
    yield put(showNotifySuccess({}));
  } catch (e) {
    yield put(errorsGlobalError(e as ErrorEvent));
  }
};

export const fetchPricingGroupSaga = function* ({
  payload
}: ISagaAction<{ id?: string }>): SagaIterator {
  try {
    const selectedPrisingList = yield select(getSelectedPriceListSelector);
    const currentListId = payload?.id || selectedPrisingList;
    const response: { result: IPricingGroup[] } = yield call(Api.Shop.fetchPricingGroups, {
      pricingListId: currentListId
    });

    ApiErrors.checkOnApiError(response);

    yield put(
      fetchProductUserPricingGroupsListSuccessAction(
        Array.isArray(response?.result) ? response.result : []
      )
    );
  } catch (e) {
    // If this fails the currentPrice list is an old one without groups so we invalid the cached ones
    yield put(fetchProductUserPricingGroupsListSuccessAction([]));
    yield put(errorsGlobalError(e as ErrorEvent));
  }
};
export const updatePricinGroupSaga = function* ({
  payload
}: ISagaAction<IUpdatePricingGroup>): SagaIterator {
  const groups: IPricingGroup[] = yield select(getPricingGroups);
  const originalGroups = groups;
  try {
    // Optimistic update
    const updatedGroups = groups.map((group: IPricingGroup) =>
      group.id === payload.id
        ? {
            ...group,
            ...payload
          }
        : group
    );
    yield put(fetchProductUserPricingGroupsListSuccessAction([...updatedGroups]));

    const response = yield call(Api.Shop.updatePricingGroup, payload);
    ApiErrors.checkOnApiError(response);
    yield put(showNotifySuccess({}));
  } catch (e) {
    yield put(fetchProductUserPricingGroupsListSuccessAction(originalGroups));
    yield put(errorsGlobalError(e as ErrorEvent));
  }
};

export const createDigitalProductSaga = function* ({
  payload
}: ISagaAction<ICreateDigitalProduct>): SagaIterator {
  try {
    const response = yield call(Api.Shop.createNewDigitalProduct, payload);
    ApiErrors.checkOnApiError(response);
    const selectedPricingList = yield select(getSelectedPriceListSelector);
    yield put(fetchUserPricingAction(selectedPricingList));
  } catch (e) {
    yield put(errorsGlobalError(e as ErrorEvent));
  }
};

export const deleteDigitalUserPricingSaga = function* ({
  payload
}: ISagaAction<IDeleteDigitalUserPricing>): SagaIterator {
  const prices: IPriceItem[] = yield select(getProductUserPricing);
  try {
    const updatedPrices = prices.filter((price) => price._id !== payload.userPricingId);

    yield put(fetchUserPricingSuccessAction([...updatedPrices]));
    const response = yield call(Api.Shop.deleteDigitalUserPricing, payload);
    ApiErrors.checkOnApiError(response);
    yield put(showNotifySuccess({}));
  } catch (e) {
    yield put(fetchUserPricingSuccessAction([...prices]));
    yield put(errorsGlobalError(e as ErrorEvent));
  }
};

export const fetchUserPricingSaga = function* ({
  payload
}: ISagaAction<{ id?: string }>): SagaIterator {
  try {
    const selectedPricingList = yield select(getSelectedPriceListSelector);
    const currentListId = payload?.id || selectedPricingList;

    const response = yield call(Api.Shop.fetchProductUserPricing, {
      productPricingList_id: currentListId
    });
    ApiErrors.checkOnApiError(response);
    yield put(fetchUserPricingSuccessAction(Array.isArray(response) ? response : []));
  } catch (e) {
    yield put(fetchUserPricingSuccessAction([]));
    yield put(errorsGlobalError(e as ErrorEvent));
  }
};

export const updateProductUserPricingsSaga = function* ({
  payload
}: ISagaAction<IUpdateProductUserPricings>): SagaIterator {
  // this is shitty but i don't know a better solution
  const bestsellerUpdate = payload.productUserPricings.find((update) =>
    update.updates.find((up) => up.key === 'bestseller' && up.value === true)
  );
  const prices = yield select(getProductUserPricing);
  // optimistic update
  try {
    let updatedPrices = prices.map((price: IPriceItem) => {
      const data = payload.productUserPricings.find(
        (updatedPricing) => updatedPricing.id === price._id
      );
      if (data) {
        const updatedFields: any = {};
        data.updates.forEach((entry) => {
          updatedFields[entry.key] = entry.value;
        });

        return { ...price, ...updatedFields };
      }

      return price;
    });
    if (bestsellerUpdate) {
      updatedPrices = updatedPrices.map((price: IPriceItem) => {
        if (price._id !== bestsellerUpdate.id) {
          return { ...price, bestseller: false };
        }

        return price;
      });
    }

    yield put(fetchUserPricingSuccessAction([...updatedPrices]));

    const response = yield call(Api.Shop.updateProductUserPricings, payload);
    ApiErrors.checkOnApiError(response);

    yield put(showNotifySuccess({}));
  } catch (error) {
    yield put(fetchUserPricingSuccessAction([...prices]));
    yield put(errorsGlobalError(error as ErrorEvent));
  }
};
export const updatePriceValueSaga = function* ({
  payload
}: ISagaAction<IUpdatePriceValue>): SagaIterator {
  const prices = yield select(getProductUserPricing);
  try {
    const updatedPrices = prices.map((price: IPriceItem) =>
      price._id === payload.id
        ? {
            ...price,
            [payload.data.key]: payload.data.value
          }
        : price
    );
    yield put(fetchUserPricingSuccessAction([...updatedPrices]));
    if (payload.local) return;

    yield delay(1000);
    const response = yield call(Api.Shop.updateProductUserPricing, payload);
    ApiErrors.checkOnApiError(response);

    yield put(showNotifySuccess({}));
  } catch (e) {
    yield put(errorsGlobalError(e as ErrorEvent));
  }
};

export const fetchUserAccountBalanceSaga = function* (): SagaIterator {
  try {
    const response = yield call(Api.Shop.getUserAccountBalance);
    ApiErrors.checkOnApiError(response);
    yield put(fetchUserAccountBalanceSuccessAction(response));
  } catch (e) {
    yield put(errorsGlobalError(e as ErrorEvent));
  }
};

export const fetchCouponsListSaga = function* (): SagaIterator {
  try {
    const response = yield call(Api.Shop.fetchCouponsList);
    ApiErrors.checkOnApiError(response);
    yield put(fetchCouponsSuccessAction(response.result.list));
  } catch (e) {
    yield put(errorsGlobalError(e as ErrorEvent));
  }
};

const getCouponData = function* (couponData: IShopCoupon): SagaIterator<IShopCoupon> {
  const user = yield select(getUser);
  const { code, name, validUntilDate, discounts, minimumOrderValue } = couponData;
  const cleanedDiscounts = discounts.map((discount) =>
    pickBy(discount, (value) => {
      // Check if value is empty, 0, or null
      if (typeof value === 'string' || typeof value === 'number' || value === null) {
        return ['', 0, null].indexOf(value) === -1;
      }

      return true;
    })
  );
  const validDate = moment(validUntilDate);

  return {
    user: user._id,
    name,
    active: true,
    code,
    minimumOrderValue,
    validUntilDate: validDate.isValid() ? moment(validUntilDate).format() : '',
    // @ts-ignore
    discounts: cleanedDiscounts
  };
};
export const createCouponSaga = function* ({ payload }: ISagaAction<IShopCoupon>): SagaIterator {
  try {
    const couponsList = yield select(getShopCouponsSelector);
    const data = yield call(getCouponData, payload);
    const response = yield call(Api.Shop.createCoupon, data);
    ApiErrors.checkOnApiError(response);
    couponsList.push(response.result);
    yield put(fetchCouponsSuccessAction([...couponsList]));
    yield put(showNotifySuccess({}));
  } catch (e) {
    yield put(errorsGlobalError(e as ErrorEvent));
  }
};

export const deleteCouponSaga = function* ({ payload }: ISagaAction<string>): SagaIterator {
  try {
    const response = yield call(Api.Shop.deleteCoupon, payload);
    ApiErrors.checkOnApiError(response);
    const couponsList = yield select(getShopCouponsSelector);
    const filteredList = couponsList.filter((coupon: IShopCoupon) => coupon.id !== payload);
    yield put(fetchCouponsSuccessAction(filteredList));
    yield put(showNotifySuccess({}));
  } catch (e) {
    yield put(errorsGlobalError(e as ErrorEvent));
  }
};

export const updateCouponSaga = function* ({ payload }: ISagaAction<IShopCoupon>): SagaIterator {
  try {
    const data = yield call(getCouponData, payload);
    const response = yield call(Api.Shop.updateCoupon, omit(data, 'user'), payload.id || '');
    ApiErrors.checkOnApiError(response);
    const couponsList = yield select(getShopCouponsSelector);
    const updatedList = couponsList.map((coupon: IShopCoupon) =>
      coupon.id === payload.id ? payload : coupon
    );
    yield put(fetchCouponsSuccessAction(updatedList));
    yield put(showNotifySuccess({}));
  } catch (e) {
    yield put(errorsGlobalError(e as ErrorEvent));
  }
};

export const fetchProductGroupsSaga = function* (): SagaIterator {
  try {
    const response = yield call(Api.Shop.getProductGroups);
    ApiErrors.checkOnApiError(response);
    yield put(fetchProductGroupsSuccessAction(response.result.list));
  } catch (e) {
    yield put(errorsGlobalError(e as ErrorEvent));
  }
};

export const fetchProductsSaga = function* (): SagaIterator {
  try {
    const response = yield call(Api.Shop.getProducts);
    ApiErrors.checkOnApiError(response);
    yield put(fetchProductsSuccessAction(response.result.list));
  } catch (e) {
    yield put(errorsGlobalError(e as ErrorEvent));
  }
};

export const fetchGlobalShopSettingsSaga = function* (): SagaIterator {
  try {
    const response = yield call(Api.Shop.getGlobalShopSettingsForCurrentUser);
    // Network errors resolve as { code: 0 } instead of rejecting; without this
    // guard the saga would overwrite the stored settings with `undefined` and
    // the toggle would dispatch a create instead of an update afterwards.
    if (response?.code === 0) return;
    ApiErrors.checkOnApiError(response);
    yield put(getGlobalShopSettingsSuccess(response.result));
  } catch (e) {
    const error = e as ApiError;
    if (error.code === 404) {
      yield put(getGlobalShopSettingsSuccess(null));

      return;
    }

    yield put(errorsGlobalError(e as ErrorEvent));
  }
};

export const createGlobalShopSettingsSaga = function* ({
  payload
}: ISagaAction<ICreateGlobalShopSettingsParams>): SagaIterator {
  try {
    const response = yield call(Api.Shop.createGlobalShopSettings, payload);
    // Network errors resolve as { code: 0 } and would otherwise fall through
    // to the success notification (SBK-9611).
    if (yield call(handleOfflineResponse, response)) return;
    ApiErrors.checkOnApiError(response);
    yield put(getGlobalShopSettingsSuccess(response.result));
    yield put(showNotifySuccess({}));
  } catch (e) {
    yield put(errorsGlobalError(e as ErrorEvent));
  }
};

export const updateGlobalShopSettingsSaga = function* ({
  payload
}: ISagaAction<IUpdateGlobalShopSettingsParams>): SagaIterator {
  try {
    const response = yield call(Api.Shop.updateGlobalShopSettings, omit(payload, 'id'), payload.id);
    // Network errors resolve as { code: 0 } and would otherwise fall through
    // to the success notification (SBK-9611).
    if (yield call(handleOfflineResponse, response)) return;
    ApiErrors.checkOnApiError(response);
    yield put(getGlobalShopSettingsSuccess(response.result));
    yield put(showNotifySuccess({}));
  } catch (e) {
    yield put(errorsGlobalError(e as ErrorEvent));
  }
};

export const fetchOrderListForUserSaga = function* ({
  payload
}: ISagaAction<IFetchOrderListForUserParams>): SagaIterator {
  try {
    const response = yield call(Api.Shop.fetchOrdersForUser, payload);
    ApiErrors.checkOnApiError(response);
    yield put(fetchOrderListForUserSuccessAction(response.result));
  } catch (e) {
    yield put(errorsGlobalError(e as ErrorEvent));
  }
};

export const cancelOrderSaga = function* ({
  payload
}: ISagaAction<ICancelOrderParams>): SagaIterator {
  try {
    const response = yield call(Api.Shop.cancelOrder, payload);
    ApiErrors.checkOnApiError(response);
    const orders = yield select(getShopOrdersSelector);
    const canceledOrder = response.result.order;
    yield put(
      fetchOrderListForUserSuccessAction({
        ...orders,
        list: orders.list.map((order: IShopOrder) => {
          if (order.id !== canceledOrder.id) return order;

          return canceledOrder;
        })
      })
    );

    if (response.result.hasCostInvoice) {
      yield put(setModalOpenState({ key: 'canceledOrderHasCostInvoiceModal', state: true }));
    }
  } catch (e) {
    yield put(errorsGlobalError(e as ErrorEvent));
  }
};

export const recordPaymentSaga = function* ({
  payload: orderId
}: ISagaAction<string>): SagaIterator {
  try {
    const response = yield call(Api.Shop.recordPayment, orderId);
    ApiErrors.checkOnApiError(response);
    const orders = yield select(getShopOrdersSelector);
    yield put(
      fetchOrderListForUserSuccessAction({
        ...orders,
        list: orders.list.map((order: IShopOrder) => {
          if (order.id !== response.result.id) return order;

          return response.result;
        })
      })
    );
  } catch (e) {
    yield put(errorsGlobalError(e as ErrorEvent));
  }
};

export const fetchOrderImagesActionSaga = function* ({
  payload
}: ISagaAction<string>): SagaIterator {
  try {
    const response = yield call(Api.Shop.fetchOrderImagesForUser, payload);
    ApiErrors.checkOnApiError(response);
    yield put(fetchOrderImagesSuccessAction(response.result));
  } catch (e) {
    yield put(errorsGlobalError(e as ErrorEvent));
  }
};

export const fetchLaboratoriesSaga = function* (): SagaIterator {
  try {
    const response = yield call(Api.Shop.fetchLaboratories);
    ApiErrors.checkOnApiError(response);
    yield put(fetchLaboratoriesSuccessAction(response.result.list));
  } catch (e) {
    yield put(errorsGlobalError(e as ErrorEvent));
  }
};

export const salesAutomationBulkUpdateSaga = function* ({
  payload
}: ISagaAction<ISalesConfigBulkUpdateAction>): SagaIterator {
  try {
    yield put(setSalesAutomationAction({ bulkUpdateInProgress: true }));
    const response = yield call(Api.Shop.salesAutomationBulkUpdate, payload);
    yield put(setSalesAutomationAction({ bulkUpdateInProgress: false }));
    ApiErrors.checkOnApiError(response);
    yield put(showNotifySuccess({}));
  } catch (e) {
    yield put(setSalesAutomationAction({ bulkUpdateInProgress: false }));
    yield put(errorsGlobalError(e as ErrorEvent));
  }
};

export const fetchPayDocuments = function* ({
  payload
}: ISagaAction<IShopPayDocumentsAction>): SagaIterator {
  try {
    const { result } = yield call(Api.Shop.fetchPayDocumnets, payload);
    yield put(setPayDocumentsSuccessAction(result));
  } catch (e) {
    yield put(errorsGlobalError(e as ErrorEvent));
  }
};

export const userHasEnoughInvoicingData = function* (): SagaIterator {
  try {
    const { result } = yield call(Api.Shop.userHasEnoughInvoicingData);
    yield put(userHasEnoughInvoicingDataSuccessAction(result.userHasEnoughInvoicingData));
  } catch (e) {
    yield put(errorsGlobalError(e as ErrorEvent));
  }
};
